import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarClock,
  DownloadCloud,
  Globe2,
  PackageCheck,
  ShoppingBag,
  Smartphone,
  Users
} from "lucide-react";
import {
  adminGetOverview,
  adminGetPublicCollectProxyPool,
  adminListAudit,
  adminListBillingOrders,
  adminListDesktopUpdateReleases
} from "../api/admin.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from "../components/ui/Table.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { Alert } from "../components/ui/Alert.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime, formatNumber } from "../lib/format.js";
import {
  formatLicensePlan,
  getLicenseExpiryState,
  licenseStatusLabel,
  licenseStatusTone
} from "../lib/license.js";
import { billingOrderStatusLabel, billingOrderStatusTone } from "../lib/billing.js";

export function DashboardPage() {
  const overviewQuery = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => adminGetOverview()
  });
  const ordersQuery = useQuery({
    queryKey: ["admin", "billing-orders", "workbench"],
    queryFn: () => adminListBillingOrders({ limit: 100 })
  });
  const proxyQuery = useQuery({
    queryKey: ["admin", "public-collect-proxy-pool"],
    queryFn: () => adminGetPublicCollectProxyPool()
  });
  const updatesQuery = useQuery({
    queryKey: ["admin", "desktop-updates", "workbench"],
    queryFn: () => adminListDesktopUpdateReleases({ includeDisabled: true, limit: 20 })
  });
  const auditQuery = useQuery({
    queryKey: ["admin", "audit", "workbench"],
    queryFn: () => adminListAudit({ limit: 6 })
  });

  if (overviewQuery.isLoading) return <FullPageSpinner />;
  if (overviewQuery.isError) {
    const error = overviewQuery.error;
    const message = error instanceof CloudApiError ? describeError(error.code) : "加载失败";
    return <Alert tone="danger">{message}</Alert>;
  }
  if (!overviewQuery.data) return null;

  const { totals, tenants, ruleBundle } = overviewQuery.data;
  const now = new Date();
  const renewalQueues = buildRenewalQueues(tenants, now);
  const pendingOrders = ordersQuery.data?.orders.filter((order) => order.status === "pending") ?? [];
  const failedOrders = ordersQuery.data?.orders.filter((order) => order.status === "failed") ?? [];
  const enabledProxyCount =
    proxyQuery.data?.pool.enabledCount ??
    proxyQuery.data?.pool.proxies.filter((proxy) => proxy.enabled).length ??
    0;
  const proxyCount = proxyQuery.data?.pool.proxies.length ?? 0;
  const enabledRelease = updatesQuery.data?.releases.find((release) => release.enabled);
  const ruleExpiry = getRuleBundleExpiry(ruleBundle.expiresAt, now);
  const priorityTenants = [...tenants]
    .sort((left, right) => {
      const leftExpiry = getLicenseExpiryState(left.license.expiresAt, left.license.status, now);
      const rightExpiry = getLicenseExpiryState(right.license.expiresAt, right.license.status, now);
      return leftExpiry.daysRemaining - rightExpiry.daysRemaining;
    })
    .slice(0, 6);

  return (
    <div>
      <PageHeader
        eyebrow="工作台 / 运营工作台"
        title="运营工作台"
        description="按客户续费、订单异常、授权设备和发布状态组织今日后台工作。"
        actions={
          <>
            <Link to="/rule-bundles">
              <Button variant="secondary">
                <PackageCheck className="h-4 w-4" />
                发布规则包
              </Button>
            </Link>
            <Link to="/tenants/new">
              <Button>
                <Users className="h-4 w-4" />
                新增客户
              </Button>
            </Link>
          </>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          label="客户总数"
          value={formatNumber(totals.tenantCount)}
          detail={`${formatNumber(totals.userCount)} 个登录账号`}
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          label="活跃设备"
          value={formatNumber(totals.activeDeviceCount)}
          detail={`${formatNumber(totals.revokedDeviceCount)} 个已撤销设备`}
          tone="success"
          icon={<Smartphone className="h-4 w-4" />}
        />
        <MetricCard
          label="待处理订单"
          value={formatNumber(pendingOrders.length + failedOrders.length)}
          detail={`${formatNumber(pendingOrders.length)} 待支付，${formatNumber(failedOrders.length)} 支付失败`}
          tone={failedOrders.length > 0 ? "danger" : "warning"}
          icon={<ShoppingBag className="h-4 w-4" />}
        />
        <MetricCard
          label="7 天内到期"
          value={formatNumber(renewalQueues.expired.length + renewalQueues.dueSoon.length)}
          detail={`${formatNumber(renewalQueues.expired.length)} 已过期需跟进`}
          tone={renewalQueues.expired.length > 0 ? "danger" : "warning"}
          icon={<CalendarClock className="h-4 w-4" />}
        />
        <MetricCard
          label="采集代理"
          value={`${formatNumber(enabledProxyCount)} / ${formatNumber(proxyCount)}`}
          detail={proxyCount > 0 ? "启用出口 / 全部出口" : "尚未配置采集代理"}
          tone={enabledProxyCount > 0 ? "success" : "warning"}
          icon={<Globe2 className="h-4 w-4" />}
        />
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.8fr)]">
        <div className="space-y-5">
          <SectionSurface>
            <SectionHeader
              title="今日待处理"
              description="按紧急程度聚合运营动作，优先处理过期、失败和即将影响桌面端的问题。"
              actions={
                <Link to="/billing/orders" className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent-hover">
                  查看订单 <ArrowRight className="h-3 w-3" />
                </Link>
              }
            />
            <div className="space-y-3 px-4 py-4">
              <WorkbenchItem
                count={renewalQueues.expired.length}
                title="已过期客户需要续费跟进"
                description={renewalQueues.expired.length > 0 ? tenantNames(renewalQueues.expired) : "暂无已过期客户"}
                tone="danger"
                badge="高优先级"
                to="/tenants"
              />
              <WorkbenchItem
                count={renewalQueues.dueSoon.length}
                title="客户将在 7 天内到期"
                description={renewalQueues.dueSoon.length > 0 ? tenantNames(renewalQueues.dueSoon) : "暂无 7 天内到期客户"}
                tone="warning"
                badge="续费提醒"
                to="/tenants"
              />
              <WorkbenchItem
                count={failedOrders.length}
                title="支付失败订单需要核对"
                description={failedOrders.length > 0 ? orderNames(failedOrders) : "暂无支付失败订单"}
                tone={failedOrders.length > 0 ? "danger" : "neutral"}
                badge="订单异常"
                to="/billing/orders"
              />
              <WorkbenchItem
                count={ruleExpiry.daysRemaining <= 7 ? 1 : 0}
                title="规则包发布准备"
                description={
                  ruleBundle.bundleVersion
                    ? `${ruleBundle.bundleVersion}，${ruleExpiry.label}`
                    : "还没有发布正式规则包"
                }
                tone={ruleExpiry.daysRemaining <= 7 ? "warning" : "info"}
                badge="规则状态"
                to="/rule-bundles"
              />
            </div>
          </SectionSurface>

          <SectionSurface>
            <SectionHeader
              title="重点客户"
              description="优先展示到期时间最近、已过期或额度接近风险的客户。"
              actions={
                <Link to="/tenants" className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent-hover">
                  查看全部客户 <ArrowRight className="h-3 w-3" />
                </Link>
              }
            />
            <div className="p-0">
              <Table className="rounded-none border-0">
                <THead>
                  <TR>
                    <TH>客户</TH>
                    <TH>授权</TH>
                    <TH>到期</TH>
                    <TH>设备</TH>
                    <TH>店铺</TH>
                    <TH>操作</TH>
                  </TR>
                </THead>
                <TBody>
                  {priorityTenants.length === 0 ? (
                    <EmptyRow colSpan={6} label="暂无客户。" />
                  ) : (
                    priorityTenants.map((tenant) => {
                      const expiryState = getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status, now);
                      return (
                        <TR key={tenant.id}>
                          <TD>
                            <Link
                              to={`/tenants/${encodeURIComponent(tenant.id)}`}
                              className="font-medium text-text-primary hover:text-accent"
                            >
                              {tenant.name}
                            </Link>
                            <div className="mt-0.5 font-mono text-xs text-text-muted">
                              {tenant.id} · {tenant.ownerEmail ?? "—"}
                            </div>
                          </TD>
                          <TD>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Badge tone={licenseStatusTone(tenant.license.status)}>
                                {licenseStatusLabel(tenant.license.status)}
                              </Badge>
                              <span className="text-xs text-text-secondary">
                                {formatLicensePlan(tenant.license.plan)}
                              </span>
                            </div>
                          </TD>
                          <TD>
                            <Badge tone={expiryState.tone}>{expiryState.label}</Badge>
                          </TD>
                          <TD>
                            <span className="text-text-primary">{formatNumber(tenant.activeDeviceCount)}</span>
                            <span className="text-text-muted"> / {formatNumber(tenant.license.deviceLimit)}</span>
                          </TD>
                          <TD>
                            <span className="text-text-primary">{formatNumber(tenant.storeCount ?? 0)}</span>
                            <span className="text-text-muted"> / {formatNumber(tenant.license.storeLimit ?? 50)}</span>
                          </TD>
                          <TD>
                            <Link
                              to={`/tenants/${encodeURIComponent(tenant.id)}`}
                              className="text-xs font-medium text-accent hover:text-accent-hover"
                            >
                              查看
                            </Link>
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </div>
          </SectionSurface>
        </div>

        <div className="space-y-5">
          <SectionSurface>
            <SectionHeader title="系统服务状态" description="影响桌面端和公开采集的关键配置。" />
            <div className="space-y-3 px-4 py-4">
              <HealthRow
                title="露天规则包"
                detail={ruleBundle.bundleVersion ?? "未发布"}
                badge={ruleBundle.source === "published" ? ruleExpiry.label : "未发布"}
                tone={ruleBundle.source === "published" && ruleExpiry.daysRemaining > 7 ? "success" : "warning"}
                icon={<PackageCheck className="h-4 w-4" />}
              />
              <HealthRow
                title="公开采集代理池"
                detail={`${formatNumber(enabledProxyCount)} 个启用，${formatNumber(proxyCount)} 个出口`}
                badge={enabledProxyCount > 0 ? "可用" : "待配置"}
                tone={enabledProxyCount > 0 ? "success" : "warning"}
                icon={<Globe2 className="h-4 w-4" />}
              />
              <HealthRow
                title="桌面端更新"
                detail={enabledRelease ? `${enabledRelease.target} / ${enabledRelease.arch} / ${enabledRelease.version}` : "暂无启用版本"}
                badge={enabledRelease ? "启用" : "未启用"}
                tone={enabledRelease ? "info" : "warning"}
                icon={<DownloadCloud className="h-4 w-4" />}
              />
            </div>
          </SectionSurface>

          <SectionSurface>
            <SectionHeader title="订单快照" description="最近订单状态分布，失败和待支付会进入待处理。" />
            <div className="grid grid-cols-3 gap-3 px-4 py-4">
              <MiniStat label="待支付" value={pendingOrders.length} tone="warning" />
              <MiniStat label="支付失败" value={failedOrders.length} tone="danger" />
              <MiniStat
                label="已支付"
                value={ordersQuery.data?.orders.filter((order) => order.status === "paid").length ?? 0}
                tone="success"
              />
            </div>
            <div className="px-4 pb-4">
              {(ordersQuery.data?.orders ?? []).slice(0, 4).map((order) => (
                <div key={order.id} className="flex items-center justify-between gap-3 border-t border-surface-border py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-text-primary">{order.tenantName}</div>
                    <div className="truncate font-mono text-xs text-text-muted">{order.id}</div>
                  </div>
                  <Badge tone={billingOrderStatusTone(order.status)}>{billingOrderStatusLabel(order.status)}</Badge>
                </div>
              ))}
            </div>
          </SectionSurface>

          <SectionSurface>
            <SectionHeader title="最近高风险审计" description="只展示近期管理员需要关注的动作。" />
            <div className="px-4 py-2">
              {(auditQuery.data?.entries ?? []).length === 0 ? (
                <div className="py-6 text-center text-sm text-text-muted">暂无审计记录。</div>
              ) : (
                auditQuery.data?.entries.map((entry) => (
                  <div key={entry.id} className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 border-b border-surface-border py-3 last:border-b-0">
                    <div className="text-xs text-text-muted">{formatDateTime(entry.createdAt)}</div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-text-primary">{formatAuditAction(entry.action)}</div>
                      <div className="truncate text-xs text-text-muted">
                        {entry.actorEmail ?? entry.adminEmail ?? entry.actorId} · {entry.tenantId ?? entry.resourceId}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </SectionSurface>
        </div>
      </div>
    </div>
  );
}

function buildRenewalQueues(
  tenants: Array<{
    name: string;
    license: { expiresAt: string; status: string };
  }>,
  now: Date
) {
  const expired = [];
  const dueSoon = [];
  for (const tenant of tenants) {
    const state = getLicenseExpiryState(tenant.license.expiresAt, tenant.license.status, now);
    if (state.daysRemaining <= 0 || tenant.license.status === "expired") {
      expired.push(tenant);
    } else if (state.daysRemaining <= 7) {
      dueSoon.push(tenant);
    }
  }
  return { expired, dueSoon };
}

function getRuleBundleExpiry(expiresAt: string | undefined | null, now: Date) {
  if (!expiresAt) return { label: "未配置过期时间", daysRemaining: 0 };
  const date = new Date(expiresAt);
  if (!Number.isFinite(date.getTime())) return { label: "过期时间无效", daysRemaining: 0 };
  const ms = date.getTime() - now.getTime();
  const daysRemaining = Math.ceil(ms / 86_400_000);
  if (ms <= 0) return { label: "已过期", daysRemaining };
  return { label: `${daysRemaining} 天后过期`, daysRemaining };
}

function tenantNames(tenants: Array<{ name: string }>): string {
  return tenants.slice(0, 4).map((tenant) => tenant.name).join("、");
}

function orderNames(orders: Array<{ tenantName: string }>): string {
  return orders.slice(0, 4).map((order) => order.tenantName).join("、");
}

function WorkbenchItem({
  count,
  title,
  description,
  tone,
  badge,
  to
}: {
  count: number;
  title: string;
  description: string;
  tone: "danger" | "warning" | "info" | "neutral";
  badge: string;
  to: string;
}) {
  const toneClass = {
    danger: "bg-status-danger/10 text-status-danger",
    warning: "bg-status-warning/10 text-status-warning",
    info: "bg-accent-subtle text-accent",
    neutral: "bg-surface-mute text-text-secondary"
  }[tone];
  const badgeTone = tone === "danger" ? "danger" : tone === "warning" ? "warning" : tone === "info" ? "info" : "neutral";
  return (
    <div className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 rounded border border-surface-border bg-surface-raised px-3 py-3">
      <div className={`grid h-9 w-9 place-items-center rounded text-sm font-semibold ${toneClass}`}>{formatNumber(count)}</div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="truncate text-sm font-medium text-text-primary">{title}</div>
          <Badge tone={badgeTone}>{badge}</Badge>
        </div>
        <div className="mt-0.5 truncate text-xs text-text-muted">{description}</div>
      </div>
      <Link to={to} className="text-xs font-medium text-accent hover:text-accent-hover">
        查看
      </Link>
    </div>
  );
}

function HealthRow({
  title,
  detail,
  badge,
  tone,
  icon
}: {
  title: string;
  detail: string;
  badge: string;
  tone: "success" | "warning" | "info";
  icon: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded border border-surface-border bg-surface-raised px-3 py-3">
      <div className="grid h-8 w-8 place-items-center rounded bg-surface-mute text-text-secondary">{icon}</div>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-text-primary">{title}</div>
        <div className="mt-0.5 truncate text-xs text-text-muted">{detail}</div>
      </div>
      <Badge tone={tone}>{badge}</Badge>
    </div>
  );
}

function MiniStat({
  label,
  value,
  tone
}: {
  label: string;
  value: number;
  tone: "success" | "warning" | "danger";
}) {
  const toneClass = {
    success: "text-status-success",
    warning: "text-status-warning",
    danger: "text-status-danger"
  }[tone];
  return (
    <div className="rounded border border-surface-border bg-surface-raised px-3 py-2.5">
      <div className="text-xs text-text-muted">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${toneClass}`}>{formatNumber(value)}</div>
    </div>
  );
}

function formatAuditAction(action: string): string {
  const labels: Record<string, string> = {
    "tenant.create": "新增客户",
    "tenant.profile.update": "更新客户资料",
    "tenant.license.update": "更新授权信息",
    "tenant.device.revoke": "吊销授权设备",
    "tenant.user.password.reset": "重置登录账号密码",
    "system.public_collect_proxy.update": "更新公开采集代理池",
    "billing.settings.update": "更新支付设置",
    "rule_bundle.publish": "发布规则包",
    "desktop_update.publish": "发布桌面更新",
    "desktop_update.upload_publish": "上传并发布桌面更新",
    "desktop_update.update": "更新桌面更新",
    "desktop_update.enable": "启用桌面更新",
    "desktop_update.disable": "停用桌面更新"
  };
  return labels[action] ?? action;
}
